/**
 * @fileoverview Type definitions and constants for the MetadataEditModal component.
 * Contains shared interfaces, type mappings, and category configurations.
 */

/**
 * Represents an item from the dropdown data source.
 * Used for linking metadata to existing AIIO elements.
 */
export interface DropdownItem {
  /** Unique identifier for the item */
  readonly id: number;
  /** API resource URI for the item */
  readonly resource_uri: string;
  /** Generic name field (used by resources, requirements, etc.) */
  readonly name?: string;
  /** Role-specific name field */
  readonly role_name?: string;
  /** Department-specific name field */
  readonly department_name?: string;
  /** Location-specific name field */
  readonly location_name?: string;
  /** Working group-specific title field */
  readonly title?: string;
}

/**
 * Represents a metadata item being edited in the modal.
 */
export interface MetadataItem {
  /** Unique identifier for the metadata item */
  id: string;
  /** Display name of the item */
  name: string;
  /** Optional title text */
  title?: string;
  /** Optional description text */
  description?: string;
  /** ID of linked existing AIIO item, if any */
  linkedId?: number;
  /** ID of parent item (e.g., parent resource group, orgchart) */
  parentId?: number;
  /** Original extracted name from document (present when linkedId exists) */
  extracted_name?: string;
}

/**
 * Props for the MetadataEditModal component.
 */
export interface MetadataEditModalProps {
  /** Whether the modal is currently visible */
  readonly isOpen: boolean;
  /** Callback function to close the modal */
  readonly onClose: () => void;
  /** Display title of the metadata category (e.g., "Departement", "Role") */
  readonly categoryTitle: string;
  /** Internal category key (e.g., "departments", "roles") */
  readonly categoryKey: string;
  /** Index of the item in the form array */
  readonly itemIndex: number;
  /** The metadata item being edited */
  readonly item: MetadataItem;
}

/**
 * Mapping of category keys to their corresponding dropdown data keys.
 * Used to retrieve the correct data from the dropdown data source.
 */
export const CATEGORY_TO_DROPDOWN_KEY: Record<string, string> = {
  departments: "orgUnitsAndStaffPositions",
  roles: "roles",
  resources: "resources",
  locations: "locations",
  working_groups: "workingGroups",
  requirements: "requirements",
  resourcegroups: "resourcegroups",
  orgcharts: "orgcharts",
  requirement_min_chapters: "requirement_min_chapters",
};

/**
 * Mapping of category keys to the name field in their dropdown items.
 * Different dropdown items use different field names for their display value.
 */
export const CATEGORY_TO_NAME_FIELD: Record<string, string> = {
  departments: "department_name",
  roles: "name",
  resources: "name",
  locations: "location_name",
  working_groups: "title",
  requirements: "name",
  resourcegroups: "name",
  orgcharts: "name",
  requirement_min_chapters: "name",
};

/**
 * Mapping of category keys to their translation keys for singular labels.
 * Used for UI display and user-facing text.
 */
export const CATEGORY_TO_LABEL_KEY: Record<string, string> = {
  departments: "documentReview.metadata.department",
  roles: "documentReview.metadata.role",
  resources: "documentReview.metadata.resource",
  locations: "documentReview.metadata.location",
  working_groups: "documentReview.metadata.workgroup",
  requirements: "documentReview.metadata.requirement",
};
